import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser, signOut, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const USERS_API = 'https://z51smv2b7g.execute-api.ap-southeast-2.amazonaws.com/prod/users';

function syncUser(payload) {
  fetch(USERS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product: 'GapGo', ...payload }),
  }).catch(() => {});
}

// Build our user object from the Cognito session.
//
// After Google sign-in the profile (email/name) lives in the ID token claims, so
// we read those instead of calling fetchUserAttributes(). fetchUserAttributes()
// hits the Cognito GetUser API, which needs the `aws.cognito.signin.user.admin`
// scope — Hosted-UI / social access tokens don't carry it, so it throws and would
// leave the guest stuck as "not signed in". Only getCurrentUser() has to succeed.
async function buildUserData() {
  const current = await getCurrentUser();
  let email = '';
  let name = '';
  try {
    const session = await fetchAuthSession();
    const claims = session.tokens?.idToken?.payload || {};
    email = claims.email || '';
    name = claims.name || claims.given_name || '';
  } catch {
    /* fall back to defaults below */
  }
  if (!name) name = email ? email.split('@')[0] : 'Guest';
  const storedPhone = localStorage.getItem('guestPhone_' + current.userId) || '';
  return { userId: current.userId, name, email, phone: storedPhone };
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const userData = await buildUserData();
      setUser(userData);
      syncUser({ userId: userData.userId, email: userData.email, name: userData.name, ...(userData.phone && { phone: userData.phone }) });
    } catch {
      setUser(null);
    }
    // IMPORTANT: do NOT redirect from here. On the OAuth return URL ("/?code=...")
    // Amplify may still be exchanging the authorization code for tokens — navigating
    // away now aborts that exchange and the guest is never actually signed in.
    // AuthCallback owns the redirect and only fires it AFTER the session exists.
    setLoading(false);
  }, []);

  // Like loadUser but returns success/failure and never clears user — safe to poll.
  const refreshAuth = useCallback(async () => {
    try {
      const userData = await buildUserData();
      setUser(userData);
      syncUser({ userId: userData.userId, email: userData.email, name: userData.name, ...(userData.phone && { phone: userData.phone }) });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    loadUser();
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') loadUser();
      if (payload.event === 'signedOut') setUser(null);
    });
    return unsubscribe;
  }, [loadUser]);

  // provider: 'Google' | 'Apple'. Both are federated on the shared Cognito pool
  // (see REDSPEED.md §4) and both are enabled on the GapGo app client — a
  // provider missing from the CLIENT fails even when it exists on the pool,
  // which is exactly how the website's Apple button stayed broken for months.
  const login = async (provider = 'Google') => {
    try {
      // Save only the PATH (not query string) — avoids mismatch after the redirect.
      localStorage.setItem('oauth_return_path', window.location.pathname);
      await signInWithRedirect({ provider });
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const logout = () => {
    localStorage.removeItem('oauth_return_path');
    signOut();
  };

  const savePhone = (phone) => {
    if (!user) return;
    localStorage.setItem('guestPhone_' + user.userId, phone);
    setUser(prev => ({ ...prev, phone }));
    syncUser({ userId: user.userId, email: user.email, name: user.name, phone });
  };

  const hasPhone = Boolean(user?.phone);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, savePhone, hasPhone, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
